import type { StorefrontDiscountedPrice } from '@erp/shared';
import { useTranslation } from 'react-i18next';

import { formatMoney } from '../../lib/money.js';

/**
 * Automatic-discount display for the public store.
 *
 * A discount is BAKED INTO the price the server hands out: `price` is always the
 * real charged amount. Beside it the server sends what the shopper would have
 * paid (`originalPrice`) plus a rounded `discountPercent` for the badge. Nothing
 * here recomputes a price — we only render the pair, so the shelf edge can never
 * disagree with the till.
 */

/**
 * Any server-priced thing: a product summary/detail, a packaging unit, a variant.
 * The discount fields stay optional (as the DTO declares them) so a store with
 * the module off — or a tab still running against an older API — simply shows
 * nothing.
 */
export interface DiscountedPrice extends StorefrontDiscountedPrice {
  price: string;
}

export interface Saving {
  /** Pre-discount price, in the same denomination as the price on screen. */
  originalPrice: string;
  /** Money taken off ONE of whatever is priced — drives the "you saved" line. */
  amount: string;
  /** Whole percent off, for the badge. */
  percent: number;
}

/**
 * What to render beside a price, or null when there is nothing to show.
 *
 * `factor` scales BOTH sides for the one place the storefront multiplies a
 * per-piece price by a packaging factor itself (an option/variant sold by the
 * pack — see the product page): the struck-through number then always matches
 * the price on screen, and the percentage is unaffected by the scaling.
 */
export function resolveSaving(entry: DiscountedPrice | null | undefined, factor = 1): Saving | null {
  if (!entry?.originalPrice) return null;
  const scale = Number(factor) || 1;
  const original = Number(entry.originalPrice) * scale;
  const price = Number(entry.price) * scale;
  // Never invent a saving out of odd data — only a strictly cheaper price counts.
  if (!Number.isFinite(original) || !Number.isFinite(price) || original <= price) return null;
  const percent = entry.discountPercent && entry.discountPercent > 0
    ? entry.discountPercent
    : Math.round(((original - price) / original) * 100);
  return {
    // Unscaled prices are passed through untouched so the string stays exactly
    // the server's (no float noise in the common case).
    originalPrice: scale === 1 ? entry.originalPrice : String(original),
    amount: String(original - price),
    percent,
  };
}

/**
 * The discount pill. One badge per tile: the caller places it (the storefront's
 * badges are absolutely positioned) and this owns the wording.
 */
export function DiscountBadge({ percent, className = '' }: { percent: number; className?: string }): JSX.Element {
  const { t } = useTranslation('cms');
  // dir="auto" lets Arabic ("خصم 15%") and English ("-15%") each resolve their
  // own direction from the label rather than inheriting the page's.
  return (
    <span dir="auto" className={className}>
      {t('store.shop.discount', { percent, defaultValue: '-{{percent}}%' })}
    </span>
  );
}

/**
 * The pre-discount price, struck through, for beside the live one. The number is
 * isolated so it can't visually merge with the price next to it in an RTL page,
 * and screen readers get a label instead of a bare crossed-out amount.
 *
 * Colour is left to the caller (it inherits an already-muted context in most
 * places, and the lightbox is on black) — a default here would fight the
 * caller's class rather than reliably lose to it.
 */
export function OriginalPrice({
  amount,
  currency,
  className = '',
}: { amount: string; currency: string; className?: string }): JSX.Element {
  const { t, i18n } = useTranslation('cms');
  return (
    <s className={`line-through ${className}`}>
      <span className="sr-only">{t('store.product.originalPrice', { defaultValue: 'Original price' })} </span>
      <bdi>{formatMoney(amount, { locale: i18n.language, currency })}</bdi>
    </s>
  );
}
